import {ChangeDetectionStrategy, Component, OnDestroy} from '@angular/core';
import {ContentService} from "../_services/content.service";
import {AuthService} from "../_services/auth.service";
import {debounceTime, distinctUntilChanged, of, Subject, Subscription, switchMap, tap} from "rxjs";
import {catchError} from "rxjs/operators";
import {SnackbarService} from "../_services/snackbar.service";
import {User} from "../_types/user";
import {boxType, TimelineCard} from "../_types/memorybox";

@Component({
    selector: 'app-people',
    templateUrl: './people.component.html',
    styleUrl: './people.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PeopleComponent implements OnDestroy {
    loading: boolean = true;
    user: User | undefined;
    timelines: TimelineCard[] = []
    searchTerm: string | undefined;
    contactEmail: string | undefined;
    boxCount: number = 0;
    page: number = 0;
    isRaa: boolean = false;
    protected readonly boxType = boxType;
    private searchTermSubject: Subject<string> = new Subject<string>();
    private readonly searchSubscription: Subscription;

    constructor(private contentService: ContentService,
                private authService: AuthService,
                private snackbarService: SnackbarService) {
        this.authService.getDynamicSettings().then((r): void => {
            this.isRaa = r.is_raa;
        });
        this.getTimelines();
        this.authService.refreshUser().then(() => {
            this.authService.getUser().subscribe({
                next: r => {
                    this.user = r;
                }
            })
        });
        this.authService.getDynamicSettings().then((r): void => {
            this.contactEmail = r.contact_email;
        });

        this.searchSubscription = this.searchTermSubject.pipe(
            debounceTime(300),
            distinctUntilChanged(),
            tap((): void => {
                this.loading = true;
                this.page = 0;
            }),
            switchMap(value => {
                return this.contentService.getRelatedTimelines(this.page, 12, value).pipe(
                    catchError(e => {
                        this.loading = false;
                        this.snackbarService.openSnackBar(e, 'error');
                        return of({count: 0, results: [] as TimelineCard[]});
                    })
                );
            })
        ).subscribe({
            next: (r): void => {
                this.timelines = r.results;
                this.boxCount = r.count;
                this.loading = false;
            },
            error: (e): void => {
                this.loading = false;
                this.snackbarService.openSnackBar(e, 'error');
            }
        });
    }

    get isProfilePictureOnlyMode(): boolean {
        return !!this.user && (this.user.role.name === 'Adoptive Family' || !this.user.role.can_access_profiles);
    }

    get profileButtonLabel(): string {
        return this.isProfilePictureOnlyMode ? 'Change Profile Picture' : 'View Profile';
    }

    showProfileButton(timeline: TimelineCard): boolean {
        if (!this.user) {
            return false;
        }

        if (this.isRaa && this.user.role.name === 'Adoptive Family') {
            const allowedBoxTypes: boxType[] = [
                boxType.Child,
                boxType.YoungPerson,
                boxType.Adult,
            ];

            if (!allowedBoxTypes.includes(timeline.box_type)) {
                return false;
            }

            return timeline.current_permission === 'View, Comment and Add';
        }

        if (this.isRaa && this.user.id === timeline.owner?.id) {
            return false;
        }

        return this.user.role.can_access_profiles || timeline.owner?.id === this.user.id;
    }

    keyup(): void {
        if (this.searchTerm != null) {
            this.searchTermSubject.next(this.searchTerm);
        } else {
            this.searchTermSubject.next('');
        }
    }

    ngOnDestroy(): void {
        if (this.searchSubscription) {
            this.searchSubscription.unsubscribe();
        }
    }

    clearSearch(): void {
        this.searchTerm = '';
        this.searchTermSubject.next(this.searchTerm);
    }

    getTimelines(newPage?: boolean): void {
        this.loading = true;
        this.contentService.getRelatedTimelines(this.page, 12, this.searchTerm).subscribe({
            next: r => {
                this.boxCount = r.count;
                if (newPage) {
                    this.timelines = this.timelines.concat(r.results);
                } else {
                    this.timelines = r.results;
                }
                this.loading = false;
            }, error: error => {
                this.snackbarService.openSnackBar(error, 'error')
                console.error(error)
            }
        });

    }

    getMoreTimelines(): void {
        this.loading = true;
        this.page++
        this.getTimelines(true);
    }
}
